import { viewTechDocRouteRef } from '@agilelab/plugin-wb-builder-catalog';
import { WitboostTemplate } from '@agilelab/plugin-wb-builder-common';
import {
  AutoFixHighIcon,
  FormatListBulletedOffIcon,
  Severity,
  WbInfoTooltip,
  useTemplateIcon,
  WbFavoriteEntityButton,
  WbTagsArray,
  WbTruncatedTypographyWrapper,
} from '@agilelab/plugin-wb-platform';
import { DEFAULT_NAMESPACE, Entity } from '@backstage/catalog-model';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  ScmIntegrationIcon,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import { getEntitySourceLocation } from '@backstage/plugin-catalog-react';
import {
  Box,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  useTheme,
} from '@material-ui/core';
import FormatListBulleted from '@material-ui/icons/FormatListBulleted';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import GetAppIcon from '@material-ui/icons/GetApp';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { MouseEvent, useState } from 'react';
import { useScaffolderPageContext } from './context/useScaffolderPageContext';
import { CustomTemplate, findDuplicatesCustomTemplates } from './utils';
import {
  copyTemplateRef,
  downloadTemplateDefaultValues,
  COPY_TEMPLATE_REF,
  DOWNLOAD_DEFAULT_VALUES,
} from '../TemplateList/templateActions';

const useStyles = makeStyles({
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
});

export const NameColumn = (entity: Entity) => {
  const title = entity.metadata.title;
  const icon = useTemplateIcon(entity, 'small');
  return (
    <Box style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
      <WbFavoriteEntityButton entity={entity} />
      {icon}
      <WbTruncatedTypographyWrapper
        value={title ?? ''}
        style={{ fontWeight: 700 }}
      />
    </Box>
  );
};

export const TypeColumn = (entity: Entity) => {
  return (
    <WbTruncatedTypographyWrapper
      value={
        (entity?.metadata.classDetails as Record<string, any>).displayName ??
        'Others'
      }
    />
  );
};

export const DescriptionColumn = (entity: Entity) => {
  return (
    <WbTruncatedTypographyWrapper value={entity.metadata.description || ''} />
  );
};

export const TagsColumn = (entity: Entity) => {
  return (
    <WbTagsArray
      tags={(entity.metadata.tags || []).map(t => ({ tagFQN: t }))}
    />
  );
};

export const ActionColumn = ({
  witboostCustomTemplates,
  entity,
  onOpenDocs,
}: {
  witboostCustomTemplates: Record<string, CustomTemplate[]>;
  entity: Entity;
  onOpenDocs?: (entity: Entity) => void;
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // CODE LINK
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const sourceLocation = getEntitySourceLocation(entity, scmIntegrationsApi);

  // TECH DOCS LINK
  const viewTechDoc = useRouteRef(viewTechDocRouteRef);
  const viewTechDocsAnnotation =
    entity.metadata.annotations?.['backstage.io/techdocs-ref'];
  const viewTechDocsLink =
    !!viewTechDocsAnnotation &&
    !!viewTechDoc &&
    viewTechDoc({
      namespace: entity.metadata.namespace ?? DEFAULT_NAMESPACE,
      kind: entity.kind,
      name: entity.metadata.name,
    });

  // REVERSE PROVISIONING LINK
  const customTemplates =
    witboostCustomTemplates[
      (
        entity as WitboostTemplate
      ).spec.steps[0].input.values.useCaseTemplateId?.toLocaleLowerCase()
    ];
  const reverseProvisioningTemplateUrl = customTemplates?.find(
    customTemplate => customTemplate.kind === 'reverseprovisioningtemplate',
  );

  // EDIT TEMPLATE LINK
  const editTemplateUrl = findDuplicatesCustomTemplates(
    'edittemplate',
    customTemplates,
  );

  return (
    <div>
      <IconButton
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label={open ? 'close' : 'more'}
        onClick={handleClick}
        style={{
          width: '32px',
          height: '32px',
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={e => {
          e.stopPropagation();
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {sourceLocation && (
          <MenuItem
            className={classes.menuItem}
            onClick={() => {
              window.location.href = sourceLocation.locationTargetUrl;
            }}
          >
            <ScmIntegrationIcon type={sourceLocation.integrationType} />
            Open Repository
          </MenuItem>
        )}
        {editTemplateUrl.result && editTemplateUrl.result.ref && (
          <MenuItem
            onClick={() => {
              window.location.href = editTemplateUrl.result!.ref;
            }}
            className={classes.menuItem}
          >
            <FormatListBulleted fontSize="small" />
            Editor Wizard
          </MenuItem>
        )}
        {reverseProvisioningTemplateUrl && (
          <MenuItem
            onClick={() => {
              window.location.href = reverseProvisioningTemplateUrl.ref;
            }}
            className={classes.menuItem}
          >
            <AutoFixHighIcon />
            Reverse Provisioning
          </MenuItem>
        )}
        {viewTechDocsLink && (
          <MenuItem
            onClick={() => {
              if (onOpenDocs) {
                onOpenDocs(entity);
              }
              handleClose();
            }}
            style={{ borderTop: `1px solid ${theme.palette.grey[300]}` }}
            className={classes.menuItem}
          >
            <InfoIcon fontSize="small" />
            Documentation
          </MenuItem>
        )}
        <MenuItem
          className={classes.menuItem}
          onClick={() => {
            copyTemplateRef(entity);
          }}
        >
          <FileCopyIcon fontSize="small" />
          {COPY_TEMPLATE_REF.tableFormat}
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          onClick={() => {
            downloadTemplateDefaultValues(entity);
          }}
        >
          <GetAppIcon fontSize="small" />
          {DOWNLOAD_DEFAULT_VALUES.tableFormat}
        </MenuItem>
      </Menu>
    </div>
  );
};

export const ErrorColumn = ({
  witboostCustomTemplates,
  entity,
}: {
  witboostCustomTemplates: Record<string, CustomTemplate[]>;
  entity: Entity;
}) => {
  const { errors } = useScaffolderPageContext();

  const customTemplates =
    witboostCustomTemplates[
      (
        entity as WitboostTemplate
      ).spec.steps[0].input.values.useCaseTemplateId?.toLocaleLowerCase()
    ];

  const editTemplateUrl = findDuplicatesCustomTemplates(
    'edittemplate',
    customTemplates,
  );

  const error = errors?.find(e => e.entity_id === entity.metadata.uid);

  return (
    <>
      {error?.errorMessage && (
        <WbInfoTooltip severity={Severity.Error} title={error.errorMessage} />
      )}
      {editTemplateUrl.error && (
        <WbInfoTooltip
          severity={Severity.Error}
          title={editTemplateUrl.errorMessage ?? 'error'}
          icon={<FormatListBulletedOffIcon />}
          to={`/catalog/${
            entity?.metadata.namespace ?? 'default'
          }/${entity?.kind?.toLocaleLowerCase()}/${entity?.metadata.name}`}
        />
      )}
    </>
  );
};
